import { useActivation } from '@context/MobileOne';
import { ButtonCheckbox } from '@core/ButtonCheckbox';
import { ButtonPrimary, ScrollToTop } from '@core/index';
import { CardPayment } from '@core/CardPayment';
import { CMSLink } from '@core/CMSLink';
import { LogoExternal } from '@core/LogoExternal';
import { NavigationPageTitle } from '@core/NavigationPageTitle';
import { Notification } from '@core/Notification';
import { useStaticContent } from '@dom-digital-online-media/dom-static-content-sdk';
import { AppPageContentWrapper } from '@part/AppPageContentWrapper';
import { tA11y } from '@utils/a11y/a11yHelpers';
import {
  appAlert,
  appAutoTopUpType,
  appButtonType,
  appCardStyle,
  appCheckboxSize,
  appIconsPath,
  appImages,
  appTopUpType
} from '@utils/globalConstant';
import React, { useEffect, useState } from 'react';

export function Payment() {
  // Contexts
  const {
    isLoading,
    isPaymentMethodLoading,
    paymentMethod,
    processToPaymentForm,
    setProcessToPaymentForm,
    processToPayment
  } = useActivation();
  const { staticContentData } = useStaticContent();

  // State
  const [paymentList, setPaymentList] = useState([]);
  const [check, setCheck] = useState(false);
  const [errorPayment, setErrorPayment] = useState(false);
  const [errorCheck, setErrorCheck] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');

  // const
  const checkboxLabel1Content = tA11y('ek_global_top_up_payment_chckbx_txt1');
  const buttonContent = tA11y('ek_reg_auto_top_up_payment_btn1');
  const paymentErrorContent = tA11y('ek_global_payment_err');
  const checkBoxErrorContent = tA11y('ek_global_payment_chckbx_err');

  // Functions
  const handlePaymentMethod = (method) => {
    setProcessToPaymentForm({
      ...processToPaymentForm,
      paymentMethodName: method.name
    });
    setSelectedPaymentMethod(method.displayName);
    setErrorPayment(false);
  };

  const handleButtonClick = () => {
    const isError = !selectedPaymentMethod || !check;
    setErrorPayment(!selectedPaymentMethod);
    setErrorCheck(!check);

    if (!isError) {
      processToPayment({ ...processToPaymentForm });
    }
  };

  const filterPaymentMethod = () =>
    (paymentMethod || [])
      .filter(
        ({ paymentTokenEnabled, onceOffEnabled }) =>
          (processToPaymentForm.type === appAutoTopUpType.AUTOMATIC && paymentTokenEnabled) ||
          (processToPaymentForm.type === appTopUpType.DIRECT && onceOffEnabled)
      )
      .sort((a, b) => {
        const filterList = staticContentData?.ek2_paymentMethodSortOrder?.activationTopUp || [];
        return filterList.indexOf(a.name) - filterList.indexOf(b.name);
      });

  // Hooks
  useEffect(() => {
    if (check) {
      setErrorCheck(false);
    }
  }, [check]);

  useEffect(() => {
    if (paymentMethod.length > 0) {
      setPaymentList(filterPaymentMethod());
    }
  }, [paymentMethod]);

  return (
    <>
      <ScrollToTop>
        <div className="min-dvh-100 payment-data-container">
          <AppPageContentWrapper>
            <div className="w-100">
              <NavigationPageTitle
                title="ek_reg_auto_top_up_payment_hdl1"
                replaceTitle="{AMOUNT}"
                replaceTitleBy={processToPaymentForm.productAmount}
                variant="sm"
              />
            </div>
            <div className="space-32" />
            <div className="payment-option-container mb-48">
              {!isPaymentMethodLoading &&
                paymentList.length > 0 &&
                paymentList.map((item, index) => (
                  <div className="mb-12" key={`payment-methods-${index}`}>
                    <CardPayment
                      name="paymentMethod"
                      id={item.displayName}
                      isChecked={item.displayName === selectedPaymentMethod}
                      onClick={() => handlePaymentMethod(item)}
                      iconRef={item.logoImageRef}
                      headline={tA11y(item.displayName).content}
                      text={
                        item.displayName === selectedPaymentMethod &&
                        tA11y(item.description).content
                      }
                      cardStyle={`${appCardStyle.DEFAULT}`}
                      invalid={errorPayment}
                    />
                  </div>
                ))}
              {errorPayment ? (
                <div className="">
                  <Notification type={appAlert.ERROR} message={paymentErrorContent?.content} />
                </div>
              ) : null}
            </div>
            <div className="w-100 text-primary pb-16">
              <ButtonCheckbox
                id='termsAndConditions'
                checkboxContent={checkboxLabel1Content}
                checkboxSize={appCheckboxSize.DEFAULT}
                value={check}
                onChange={setCheck}
                invalid={errorCheck}
              />
              <div className="px-20 mb-48">
                <CMSLink
                  className="px-10 pb-8 w-100"
                  iconRight={appIconsPath.ARROW_RIGHT_SMALL}
                  linkKey="ek_global_top_up_payment_chckbx_lnk1"
                />
                <CMSLink
                  className="px-10 pb-8 w-100"
                  iconRight={appIconsPath.ARROW_RIGHT_SMALL}
                  linkKey="ek_global_top_up_payment_chckbx_lnk2"
                />
                {errorCheck ? (
                  <div className="pt-20">
                    <Notification type={appAlert.ERROR} message={checkBoxErrorContent?.content} />
                  </div>
                ) : null}
              </div>
              {/* ImageContent */}
              <div className="w-100 d-flex justify-content-center mb-48">
                <LogoExternal
                  imgSrc={appImages.logoAlphacommV2}
                  imgAlt="Alphacomm"
                  externalLink="https://alphacomm.io/"
                  maxHeight="83px"
                />
              </div>
              <div className="w-100 d-flex justify-content-center pb-40">
                <div className="col-xxl-5 col-lg-5 col-10 px-0">
                  <ButtonPrimary
                    isLoading={isLoading}
                    buttonType={appButtonType.PRIMARY}
                    buttonContent={buttonContent}
                    onClick={handleButtonClick}
                  />
                </div>
              </div>
            </div>
          </AppPageContentWrapper>
        </div>
      </ScrollToTop>
    </>
  );
}

export default Payment;
